import { Link } from "@tanstack/react-router";
import { motion, AnimatePresence } from "motion/react";
import type { Club } from "@/api/clubsApi";
import { Icon } from "@/components/ui/Icon";

interface ClubCardProps {
  club: Club;
  isPinned?: boolean;
  onTogglePin?: (clubId: string) => void;
}

/**
 * ClubCard - Vertical card layout with square image
 * - 240px square image with 16px border radius
 * - Card padding: 8px top/sides, 16px bottom
 * - Club name + member count row below image
 * - Pin toggle button on right of member count row
 * - 24px card border radius
 * - Animated pin button and layout transitions
 */
export function ClubCard({ club, isPinned = false, onTogglePin }: ClubCardProps) {
  const clubId = club.id || "";

  const handlePinClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onTogglePin?.(clubId);
  };

  return (
    <Link
      to="/clubs/$identifier"
      params={{ identifier: clubId }}
      className="group flex flex-col gap-4 bg-background rounded-3xl border border-border-tertiary p-2 pb-4 w-full max-w-[300px] overflow-hidden shadow-[0px_2px_8px_0px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0px_4px_24px_0px_rgba(0,0,0,0.08)]"
    >
      {/* Club Image - Square aspect */}
      <div className="aspect-square w-full bg-surface-2 rounded-2xl relative overflow-hidden">
        {club.primary_image_medium ? (
          <img
            src={club.primary_image_medium}
            alt={club.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon name="globe" size={48} className="text-text-tertiary" />
          </div>
        )}
      </div>

      {/* Club Info - Below image */}
      <div className="flex-1 min-w-0 flex flex-col gap-1 px-2">
        <h3 className="text-lg font-medium text-text-primary truncate">
          {club.name}
        </h3>

        {/* Member count row with pin button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-body-3 text-text-secondary">
            {club.show_member_count !== false && club.member_count !== undefined && (
              <>
                <Icon name="user-group" size={12} />
                <span>
                  {club.member_count} {club.member_count === 1 ? "member" : "members"}
                </span>
              </>
            )}
          </div>

          {/* Animated pin toggle button */}
          {onTogglePin && (
            <motion.button
              onClick={handlePinClick}
              className={`w-6 h-6 flex items-center justify-center transition-colors ${
                isPinned
                  ? "text-accent-secondary hover:bg-surface-2"
                  : "text-text-disabled hover:text-text-secondary hover:bg-surface-2"
              }`}
              aria-label={isPinned ? "Unpin club" : "Pin club"}
              whileTap={{ scale: 0.85 }}
            >
              <AnimatePresence mode="wait" initial={false}>
                <motion.span
                  key={isPinned ? "pinned" : "unpinned"}
                  initial={{ scale: 0.5, rotate: -45, opacity: 0 }}
                  animate={{ scale: 1, rotate: 0, opacity: 1 }}
                  exit={{ scale: 0.5, rotate: 45, opacity: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                >
                  <Icon name={isPinned ? "thumbtack" : "thumbtack-outline"} size={14} />
                </motion.span>
              </AnimatePresence>
            </motion.button>
          )}
        </div>
      </div>
    </Link>
  );
}
